import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format, differenceInYears } from 'date-fns';
import axios from 'axios';
import { toast } from 'react-toastify';

// Get API URL from environment variables
import { API_URL } from '../../utils/apiConfig';
import { getOrganizationPhoneSettings, getPhoneValidation, REGIONS, toStoredPhone } from '../../utils/phoneUtils';
import { formatFailedActionMessage, formatSuccessActionMessage } from '../../utils/uiMessages';

// Blood groups
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const AddPatient = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clinicsLoading, setClinicsLoading] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [phoneRegion, setPhoneRegion] = useState('india');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    fetchClinics();
  }, []);

  useEffect(() => {
    getOrganizationPhoneSettings(true).then((r) => setPhoneRegion(r.region || 'india'));
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        getOrganizationPhoneSettings(true).then((r) => setPhoneRegion(r.region || 'india'));
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);
  
  // Function to fetch clinics from API
  const fetchClinics = async () => {
    setClinicsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/clinics`);
      setClinics(response.data.data || []);
    } catch (err) {
      console.error('Error fetching clinics:', err);
      toast.error(formatFailedActionMessage('load', 'clinics'));
    } finally {
      setClinicsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      patientId: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      gender: '',
      dateOfBirth: null,
      address: '',
      city: '',
      state: '',
      pincode: '',
      bloodGroup: '',
      allergies: '',
      medicalHistory: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      clinicId: '',
      occupation: '',
      referredBy: '',
    },
    validationSchema: useMemo(() => Yup.object({
      patientId: Yup.string().max(50, 'Patient ID is too long'),
      firstName: Yup.string(),
      lastName: Yup.string(),
      email: Yup.string().email('Invalid email address'),
      phone: getPhoneValidation(Yup, phoneRegion, false),
      gender: Yup.string(),
      dateOfBirth: Yup.date().nullable(),
      address: Yup.string(),
      city: Yup.string(),
      state: Yup.string(),
      pincode: Yup.string()
        .matches(/^\d{6}$/, 'Pincode must be 6 digits')
        .nullable(),
      bloodGroup: Yup.string(),
      emergencyContactName: Yup.string(),
      emergencyContactPhone: getPhoneValidation(Yup, phoneRegion, false),
      clinicId: Yup.string(),
    }).test('at-least-one-identifier', 'Name or phone number is required', function(value) {
      const { firstName, lastName, phone } = value;
      const hasName = (firstName && firstName.trim()) || (lastName && lastName.trim());
      const hasPhone = phone && phone.trim();
      return hasName || hasPhone;
    }), [phoneRegion]),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError(null);
        
        // Calculate age from date of birth
        const age = values.dateOfBirth ? differenceInYears(new Date(), new Date(values.dateOfBirth)) : null;
        
        // Format date of birth to ISO string
        const formattedDOB = values.dateOfBirth ? format(new Date(values.dateOfBirth), 'yyyy-MM-dd') : null;
        
        // Combine first and last name, or use phone if name is empty
        const fullName = `${values.firstName || ''} ${values.lastName || ''}`.trim();
        const patientName = fullName || (values.phone ? `Patient ${values.phone}` : 'New Patient');
        
        const storedPhone = values.phone ? toStoredPhone(values.phone, phoneRegion) : undefined;
        const storedEmergencyPhone = values.emergencyContactPhone ? toStoredPhone(values.emergencyContactPhone, phoneRegion) : undefined;
        const countryLabel = REGIONS[phoneRegion]?.label || 'India';
        const patientData = {
          ...values,
          name: patientName,
          patientId: values.patientId && values.patientId.trim() ? values.patientId.trim() : undefined,
          phone: storedPhone,
          emergencyContactPhone: storedEmergencyPhone,
          dateOfBirth: formattedDOB,
          age,
          registrationDate: format(new Date(), 'yyyy-MM-dd'),
          registeredClinic: values.clinicId || undefined,
          address: values.address || values.city || values.state || values.pincode ? {
            street: values.address || '',
            city: values.city || '',
            state: values.state || '',
            pincode: values.pincode || '',
            country: countryLabel
          } : undefined,
          medicalHistory: {
            allergies: values.allergies ? [values.allergies] : [],
            other: values.medicalHistory || ''
          }
        };
        
        // Remove clinicId, firstName/lastName, and individual address fields from the data
        // (these are already transformed into name and address object)
        delete patientData.clinicId;
        delete patientData.firstName;
        delete patientData.lastName;
        delete patientData.city; // Remove individual address fields (already in address object)
        delete patientData.state;
        delete patientData.pincode;
        delete patientData.allergies; // Remove individual medical history fields (already in medicalHistory object)
        delete patientData.medicalHistory; // Remove the string medical history field (already in medicalHistory object)
        
        // Remove empty string fields and undefined values to avoid validation issues
        // Create a new object with only non-empty values
        const cleanedData = {};
        Object.keys(patientData).forEach(key => {
          const value = patientData[key];
          // Keep the field if it's not empty string, null, or undefined
          if (value !== '' && value !== null && value !== undefined) {
            // For nested objects, check if they have any meaningful content
            if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
              const hasContent = Object.values(value).some(v => v !== '' && v !== null && v !== undefined);
              if (hasContent) {
                cleanedData[key] = value;
              }
            } else {
              cleanedData[key] = value;
            }
          }
        });
        
        // Replace patientData with cleaned version
        Object.keys(patientData).forEach(key => {
          if (!(key in cleanedData)) {
            delete patientData[key];
          }
        });
        Object.assign(patientData, cleanedData);
        
        // Remove empty nested objects
        if (patientData.address && (!patientData.address.street && !patientData.address.city && !patientData.address.state && !patientData.address.pincode)) {
          delete patientData.address;
        }
        
        if (patientData.medicalHistory && (!patientData.medicalHistory.allergies || patientData.medicalHistory.allergies.length === 0) && !patientData.medicalHistory.other) {
          delete patientData.medicalHistory;
        }
        
        // API call to create patient
        const response = await axios.post(`${API_URL}/patients`, patientData);
        toast.success(formatSuccessActionMessage('added', 'Patient'));
        
        // Check if we should return to appointment creation
        const returnTo = searchParams.get('returnTo');
        if (returnTo === 'appointment') {
          // Navigate back to appointment creation with the new patient ID
          const newPatientId = response.data.data._id || response.data.data.id;
          navigate(`/appointments/add?newPatientId=${newPatientId}`);
        } else {
          navigate('/patients');
        }
      } catch (err) {
        console.error('Error adding patient:', err);
        
        // Extract error message
        let errorMessage = formatFailedActionMessage('add', 'patient');
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.data?.errors) {
          if (Array.isArray(err.response.data.errors)) {
            errorMessage = err.response.data.errors.map(e => e.msg || e.message || e).join(', ');
          } else {
            errorMessage = err.response.data.errors;
          }
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  // Calculate age whenever date of birth changes
  const calculateAge = (dob) => {
    if (!dob) return '';
    return differenceInYears(new Date(), new Date(dob));
  };

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Add New Patient
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Fill in the information you have. At least a name or phone number is recommended.
        </Typography>
        <Divider sx={{ mb: 4 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                id="patientId"
                name="patientId"
                label="Patient ID (optional)"
                placeholder="Leave empty for auto (e.g. P-202602-0001)"
                value={formik.values.patientId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.patientId && Boolean(formik.errors.patientId)}
                helperText={(formik.touched.patientId && formik.errors.patientId) || 'Shown on all documents (invoices, prescriptions, etc.)'}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                id="firstName"
                name="firstName"
                label="First Name"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                id="lastName"
                name="lastName"
                label="Last Name"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth error={formik.touched.gender && Boolean(formik.errors.gender)}>
                <InputLabel id="gender-label">Gender</InputLabel>
                <Select
                  labelId="gender-label"
                  id="gender"
                  name="gender"
                  value={formik.values.gender}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Gender"
                  disabled={loading}
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
                {formik.touched.gender && formik.errors.gender && (
                  <FormHelperText>{formik.errors.gender}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date of Birth"
                  value={formik.values.dateOfBirth}
                  onChange={(date) => {
                    formik.setFieldValue('dateOfBirth', date);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      id="dateOfBirth"
                      name="dateOfBirth"
                      error={formik.touched.dateOfBirth && Boolean(formik.errors.dateOfBirth)}
                      helperText={formik.touched.dateOfBirth && formik.errors.dateOfBirth}
                      disabled={loading}
                    />
                  )}
                  disabled={loading}
                  disableFuture
                  maxDate={new Date()}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                id="age"
                label="Age"
                value={calculateAge(formik.values.dateOfBirth)}
                InputProps={{
                  readOnly: true,
                  endAdornment: <InputAdornment position="end">years</InputAdornment>,
                }}
                disabled
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="bloodGroup-label">Blood Group</InputLabel>
                <Select
                  labelId="bloodGroup-label"
                  id="bloodGroup"
                  name="bloodGroup"
                  value={formik.values.bloodGroup}
                  onChange={formik.handleChange}
                  label="Blood Group"
                  disabled={loading}
                >
                  <MenuItem value="">Not Known</MenuItem>
                  {BLOOD_GROUPS.map((group) => (
                    <MenuItem key={group} value={group}>
                      {group}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                id="occupation"
                name="occupation"
                label="Occupation"
                value={formik.values.occupation}
                onChange={formik.handleChange}
                disabled={loading}
              />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email Address"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="phone"
                name="phone"
                label="Phone Number"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={(e) => {
                  const normalized = toStoredPhone(e.target.value, phoneRegion);
                  if (normalized) formik.setFieldValue('phone', normalized);
                  formik.handleBlur(e);
                }}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone 
                  ? formik.errors.phone 
                  : (phoneRegion === 'israel' ? 'Enter Israeli phone (e.g., 050-1234567)' : 'Enter Indian phone (e.g., 9876543210)')}
                disabled={loading}
                placeholder={phoneRegion === 'israel' ? '050-1234567' : '9876543210'}
                InputProps={{
                  startAdornment: <InputAdornment position="start">+{REGIONS[phoneRegion]?.phoneCode || '91'}</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="address"
                name="address"
                label="Address"
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                disabled={loading}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                id="city"
                name="city"
                label="City"
                value={formik.values.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.city && Boolean(formik.errors.city)}
                helperText={formik.touched.city && formik.errors.city}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                id="state"
                name="state"
                label="State"
                value={formik.values.state}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.state && Boolean(formik.errors.state)}
                helperText={formik.touched.state && formik.errors.state}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                id="pincode"
                name="pincode"
                label="Pincode"
                value={formik.values.pincode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.pincode && Boolean(formik.errors.pincode)}
                helperText={formik.touched.pincode && formik.errors.pincode}
                disabled={loading}
              />
            </Grid>

            {/* Emergency Contact */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Emergency Contact
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="emergencyContactName"
                name="emergencyContactName"
                label="Emergency Contact Name"
                value={formik.values.emergencyContactName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.emergencyContactName && Boolean(formik.errors.emergencyContactName)}
                helperText={formik.touched.emergencyContactName && formik.errors.emergencyContactName}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="emergencyContactPhone"
                name="emergencyContactPhone"
                label="Emergency Contact Phone"
                value={formik.values.emergencyContactPhone}
                onChange={formik.handleChange}
                onBlur={(e) => {
                  const normalized = toStoredPhone(e.target.value, phoneRegion);
                  if (normalized) formik.setFieldValue('emergencyContactPhone', normalized);
                  formik.handleBlur(e);
                }}
                error={formik.touched.emergencyContactPhone && Boolean(formik.errors.emergencyContactPhone)}
                helperText={formik.touched.emergencyContactPhone && formik.errors.emergencyContactPhone 
                  ? formik.errors.emergencyContactPhone 
                  : (phoneRegion === 'israel' ? 'Enter Israeli phone (e.g., 050-1234567)' : 'Enter Indian phone (e.g., 9876543210)')}
                disabled={loading}
                placeholder={phoneRegion === 'israel' ? '050-1234567' : '9876543210'}
                InputProps={{
                  startAdornment: <InputAdornment position="start">+{REGIONS[phoneRegion]?.phoneCode || '91'}</InputAdornment>,
                }}
              />
            </Grid>

            {/* Medical Information */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Medical Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="allergies"
                name="allergies"
                label="Allergies"
                value={formik.values.allergies}
                onChange={formik.handleChange}
                disabled={loading}
                multiline
                rows={3}
                placeholder="List any allergies or write 'None'"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="medicalHistory"
                name="medicalHistory"
                label="Medical History"
                value={formik.values.medicalHistory}
                onChange={formik.handleChange}
                disabled={loading}
                multiline
                rows={3}
                placeholder="Any relevant medical history"
              />
            </Grid>

            {/* Clinic Information */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Clinic Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={formik.touched.clinicId && Boolean(formik.errors.clinicId)}>
                <InputLabel id="clinic-label">Clinic</InputLabel>
                <Select
                  labelId="clinic-label"
                  id="clinicId"
                  name="clinicId"
                  value={formik.values.clinicId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Clinic"
                  disabled={loading || clinicsLoading}
                >
                  {clinicsLoading ? (
                    <MenuItem disabled>Loading clinics...</MenuItem>
                  ) : clinics.length > 0 ? (
                    clinics.map((clinic) => (
                      <MenuItem key={clinic._id} value={clinic._id}>
                        {clinic.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No clinics available</MenuItem>
                  )}
                </Select>
                {formik.touched.clinicId && formik.errors.clinicId && (
                  <FormHelperText>{formik.errors.clinicId}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="referredBy"
                name="referredBy"
                label="Referred By"
                value={formik.values.referredBy}
                onChange={formik.handleChange}
                disabled={loading}
                placeholder="How did the patient hear about us?"
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12} sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate('/patients')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? 'Saving...' : 'Add Patient'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default AddPatient;